import React, { useState, useEffect } from 'react';
import { AppUser, UserRole, UserStatus } from '../../types'; // Use AppUser
import { SaveIcon, XMarkIcon, SpinnerIcon, KeyIcon } from '../../components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../firebase/firebaseConfig';
import { useConnection } from '../../context/ConnectionContext';
import { ChangeUserPasswordModal } from '../../components/admin/ChangeUserPasswordModal';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: Partial<Pick<AppUser, 'displayName' | 'username' | 'role' | 'status' | 'email'>>, userIdToUpdate: string) => Promise<void>;
  initialData?: AppUser; // Use AppUser
  mode: 'add' | 'edit';
  existingUsernames: string[];
  existingEmails: string[];
  error?: string | null; // Error from parent hook
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  mode,
  existingUsernames,
  existingEmails,
  error: parentError,
}) => {
  const { isOffline } = useConnection();
  const [formData, setFormData] = useState<Partial<AppUser>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData({
        email: initialData.email,
        displayName: initialData.displayName,
        username: initialData.username,
        role: initialData.role,
        status: initialData.status,
      });
    } else {
      // Defaults for 'add' mode, though user creation is complex and might be handled differently
      setFormData({
        email: '',
        displayName: '',
        username: '',
        role: UserRole.SIN_ROL,
        status: UserStatus.PENDING,
      });
    }
    setLocalError(null); // Clear local error when data/mode changes
  }, [initialData, mode, isOpen]); // Re-init on isOpen to reset form

  useEffect(() => {
    if (parentError) setLocalError(parentError);
  }, [parentError]);


  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setLocalError(null);
  };
  
  const validateForm = (): boolean => {
    if (!formData.email || !formData.email.trim()) {
        setLocalError("El correo electrónico es obligatorio.");
        return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
        setLocalError("El formato del correo electrónico no es válido.");
        return false;
    }
    if (mode === 'add' && existingEmails.includes(formData.email.trim().toLowerCase())) {
        setLocalError("Este correo electrónico ya está en uso.");
        return false;
    }

    if (!formData.username || !formData.username.trim()) {
        setLocalError("El nombre de usuario es obligatorio.");
        return false;
    }
    if (formData.username.trim().length < 3) {
        setLocalError("El nombre de usuario debe tener al menos 3 caracteres.");
        return false;
    }
    if (mode === 'add' && existingUsernames.includes(formData.username.trim().toLowerCase())) {
        setLocalError("Este nombre de usuario ya está en uso.");
        return false;
    }
     if (!formData.displayName || !formData.displayName.trim()) {
        setLocalError("El nombre completo es obligatorio.");
        return false;
    }
    return true;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!validateForm()) return;
    
    if (mode === 'edit' && !initialData?.id) {
        setLocalError("No se pudo identificar al usuario para editar.");
        return;
    }
    // For 'add' mode, user creation (including Auth) is complex and typically not done this way.
    // This placeholder assumes 'add' might create a Firestore profile for an *already authenticated* user
    // or that the parent `onSave` handles the full creation logic if `initialData.id` is not present.
    // The current `useAdminUsers` `saveUser` is for UPDATES.

    setIsSaving(true);
    try {
        const userId = initialData?.id; 
        if (!userId && mode === 'edit') throw new Error("ID de usuario no encontrado para la edición.");
      
        // Construct payload for update (saveUser in useAdminUsers expects this)
        const payload: Partial<Pick<AppUser, 'displayName' | 'username' | 'role' | 'status' | 'email'>> = {
            displayName: formData.displayName,
            username: formData.username,
            role: formData.role,
            status: formData.status,
            email: formData.email,
        };
        // For 'add' mode, this saveUser might need to be different or this modal isn't for full user creation.
        // Assuming 'edit' is the primary use case here based on `useAdminUsers.saveUser`.
        if (userId) {
            await onSave(payload, userId);
        } else {
             setLocalError("La adición de nuevos usuarios a través de este formulario no está completamente implementada (requiere creación de Auth).");
             // Potentially call a different onSave for add mode if it exists.
        }
      
      // Parent's onSave might handle closing or showing messages.
      // If no error from onSave (which might throw), then close.
      // However, onSave itself might set an error in the parent, which gets passed back.
      // Let parent control closure via `isOpen` prop if save is truly async and parent needs to react.
    } catch (err: any) {
      setLocalError(err.message || "Error al guardar el usuario.");
    } finally {
      setIsSaving(false);
    }
  };
  
  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm";
  const labelClass = "block text-sm font-medium text-gray-700";


  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="user-form-modal-title">
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
        <form onSubmit={handleSubmit}>
          <header className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 id="user-form-modal-title" className="text-lg font-medium leading-6 text-gray-900">
              {mode === 'edit' ? 'Editar Usuario' : 'Agregar Nuevo Usuario (Perfil Firestore)'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Cerrar modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </header>

          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {localError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3" role="alert">
                <p>{localError}</p>
              </div>
            )}
            
            <div>
              <label htmlFor="email" className={labelClass}>Correo Electrónico</label>
              <input type="email" name="email" id="email" value={formData.email || ''} onChange={handleChange} className={inputClass} required disabled={mode === 'edit'} />
              {mode === 'edit' && <p className="text-xs text-gray-500 mt-1">El correo no se puede cambiar para usuarios existentes.</p>}
            </div>

            <div>
              <label htmlFor="displayName" className={labelClass}>Nombre Completo</label>
              <input type="text" name="displayName" id="displayName" value={formData.displayName || ''} onChange={handleChange} className={inputClass} required />
            </div>
            
            <div>
              <label htmlFor="username" className={labelClass}>Nombre de Usuario (login)</label>
              <input type="text" name="username" id="username" value={formData.username || ''} onChange={handleChange} className={inputClass} required />
            </div>

            <div>
              <label htmlFor="role" className={labelClass}>Rol</label>
              <select name="role" id="role" value={formData.role || UserRole.SIN_ROL} onChange={handleChange} className={inputClass}>
                {Object.values(UserRole).map(roleValue => (
                  <option key={roleValue} value={roleValue}>{roleValue}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="status" className={labelClass}>Estado</label>
              <select name="status" id="status" value={formData.status || UserStatus.PENDING} onChange={handleChange} className={inputClass}>
                {Object.values(UserStatus).map(statusValue => (
                  <option key={statusValue} value={statusValue}>{statusValue}</option>
                ))}
              </select>
            </div>

            {mode === 'edit' && initialData && (
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <span className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Credenciales de Acceso</span>
                  <span className="text-[11px] text-gray-500">Puedes reasignar la contraseña de este usuario.</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(true)}
                  className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors"
                >
                  <KeyIcon className="w-3.5 h-3.5" />
                  Cambiar Clave
                </button>
              </div>
            )}

            {mode === 'add' && <p className="text-xs text-orange-600 mt-1">Nota: 'Agregar Usuario' aquí solo crea el perfil en Firestore. La creación de la cuenta de autenticación de Firebase debe manejarse por separado o por el flujo de registro estándar.</p>}
          </div>

          <footer className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || isOffline}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 flex items-center disabled:opacity-50"
              style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
            >
              {isSaving ? <SpinnerIcon className="w-5 h-5 mr-2" /> : <SaveIcon className="w-5 h-5 mr-2" />}
              {isSaving ? 'Guardando...' : (mode === 'edit' ? 'Guardar Cambios' : 'Agregar Usuario')}
            </button>
          </footer>
        </form>
      </div>

      <ChangeUserPasswordModal
        user={initialData || null}
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};
